interface KeyframeMarkerProps {
  x: number;
  selected?: boolean;
  color?: string;
  easing?: string;
  /** Pixels per frame — for visual snap during drag */
  pixelsPerFrame?: number;
  /** Called once on drag end with the pixel dx delta */
  onDragEnd?: (dx: number) => void;
  /** Called during drag with the pixel dx delta (for live preview of group drag) */
  onDragMove?: (dx: number) => void;
  /** Called when drag is cancelled (no meaningful movement) */
  onDragCancel?: () => void;
  /** Right-click context menu */
  onContextMenu?: (e: React.MouseEvent) => void;
  /** Click to select */
  onClick?: (e: React.MouseEvent) => void;
}

// Easing-specific icon shapes (all 8x8, centered via translate(-50%, -50%))
function easingIcon(easing: string, selected: boolean, color: string): React.CSSProperties {
  const base: React.CSSProperties = {
    position: 'absolute',
    left: 0,
    top: '50%',
    transform: 'translate(-50%, -50%)',
    width: 8,
    height: 8,
    border: `2px solid ${color}`,
    zIndex: 2,
  };

  switch (easing) {
    case 'ease-in':
      // Right-pointing triangle feel: rounded bottom-right only
      return { ...base, borderRadius: '0 50% 50% 0', background: selected ? color : '#fff' };
    case 'ease-out':
      // Left-pointing triangle feel: rounded top-left only
      return { ...base, borderRadius: '50% 0 0 50%', background: selected ? color : '#fff' };
    case 'ease-in-out':
      // Full circle (both ends eased)
      return { ...base, borderRadius: '50%', background: selected ? color : '#fff' };
    case 'step':
      // Square for step
      return { ...base, borderRadius: 1, background: selected ? color : '#fff' };
    default:
      // Diamond for linear (default)
      return { ...base, borderRadius: 1, transform: 'translate(-50%, -50%) rotate(45deg)', background: selected ? color : '#fff' };
  }
}

export function KeyframeMarker({
  x, selected = false, color = '#2563eb', easing = 'linear',
  pixelsPerFrame, onDragEnd, onDragMove, onDragCancel, onContextMenu, onClick,
}: KeyframeMarkerProps) {
  const handleMouseDown = (e: React.MouseEvent) => {
    if (!onDragEnd) return;
    e.stopPropagation();

    const diamond = e.currentTarget as HTMLDivElement;
    const startClientX = e.clientX;
    let dragged = false;

    const snapDx = (raw: number) => {
      if (pixelsPerFrame && pixelsPerFrame > 0) {
        return Math.round(raw / pixelsPerFrame) * pixelsPerFrame;
      }
      return raw;
    };

    const handleMove = (moveEvent: MouseEvent) => {
      const dx = snapDx(moveEvent.clientX - startClientX);
      if (Math.abs(dx) > 2) dragged = true;
      diamond.style.left = `${x + dx}px`;
      onDragMove?.(dx);
    };

    const handleUp = (upEvent: MouseEvent) => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      const dx = snapDx(upEvent.clientX - startClientX);
      if (dragged && Math.abs(dx) > 2) {
        onDragEnd(dx);
      } else {
        onDragCancel?.();
      }
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(e);
  };

  return (
    <div
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      onContextMenu={onContextMenu}
      style={{
        ...easingIcon(easing, selected, color),
        left: x,
        cursor: onDragEnd ? 'ew-resize' : 'pointer',
      }}
    />
  );
}
